import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BottomSheet, StatusActionButton } from '../../../shared/ui';
import { SessionV2 } from '../../../api/sessionsV2Api';
import { earningsV2Api } from '../../../api/earningsV2Api';
import { useToast } from '../../../shared/contexts/ToastContext';
import '../../paymentsV2/ui/PaymentsV2Card.css';

interface CompensationBottomSheetProps {
  isOpen: boolean;
  sessions: SessionV2[];
  siteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Ручное начисление компенсации по выбранным сменам: сумма за каждую смену + комментарий.
 * Сервер идемпотентен — по смене не больше одной компенсации; закрытость смены проверяется там же.
 */
const CompensationBottomSheet: React.FC<CompensationBottomSheetProps> = ({
  isOpen,
  sessions,
  siteId,
  onClose,
  onSuccess,
}) => {
  const { showSuccess, showError } = useToast();
  const [amountInput, setAmountInput] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setAmountInput('');
    setComment('');
    setError(null);
    const timer = window.setTimeout(() => {
      amountInputRef.current?.focus({ preventScroll: true });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    const amount = parseFloat(amountInput.replace(',', '.'));
    if (Number.isNaN(amount) || amount === 0) {
      setError('Введите сумму (отрицательная — для корректировки)');
      return;
    }
    if (sessions.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const results = await earningsV2Api.accrueSessionCompensations(
        siteId,
        sessions.map((s) => s.id),
        amount,
        comment.trim()
      );
      const created = results.filter((r) => r.status === 'created');
      const notClosed = results.filter((r) => r.status === 'not_closed').length;

      if (created.length > 0) {
        const total = created.reduce((s, r) => s + (r.amount ?? 0), 0);
        const isCorrection = amount < 0;
        const parts = [
          created.length === 1
            ? `${isCorrection ? 'Корректировка' : 'Компенсация'} ${total.toLocaleString('ru-RU')} ₽ начислена`
            : `${isCorrection ? 'Корректировка' : 'Компенсация'} начислена: ${created.length} смен на ${total.toLocaleString('ru-RU')} ₽`,
        ];
        if (notClosed > 0) parts.push(`не закрыты: ${notClosed}`);
        showSuccess(parts.join(' · '));
        onSuccess();
      } else {
        const parts: string[] = [];
        if (notClosed > 0) parts.push(`смена не закрыта: ${notClosed}`);
        showError(parts.length > 0 ? `Ничего не начислено — ${parts.join(', ')}` : 'Ничего не начислено');
        onClose();
      }
    } catch (err: unknown) {
      console.error('CompensationBottomSheet accrue error', err);
      const data = (err as { response?: { data?: { message?: string } | string } })?.response?.data;
      const message = typeof data === 'string' ? data : data?.message;
      setError(message || 'Ошибка начисления компенсации');
    } finally {
      setIsSubmitting(false);
    }
  }, [amountInput, comment, sessions, siteId, showSuccess, showError, onSuccess, onClose]);

  const title =
    sessions.length === 1 ? 'Компенсация за выезд' : `Компенсация за выезд · ${sessions.length} смен`;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title} size="auto" showHandle allowSwipeToClose>
      <form
        className="create-payment-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line" />
            <span>{error}</span>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="compensation-amount">
            {sessions.length === 1 ? 'Сумма, ₽ *' : 'Сумма за каждую смену, ₽ *'}
            <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: '0.85em' }}>
              отрицательная — корректировка
            </span>
          </label>
          <input
            ref={amountInputRef}
            id="compensation-amount"
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
            placeholder="Например, 500 или −200"
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="compensation-comment">Комментарий</label>
          <textarea
            id="compensation-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="За что компенсация (например, «бензин»)"
            className="form-textarea"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <StatusActionButton type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Отмена
          </StatusActionButton>
          <StatusActionButton
            type="submit"
            variant="primary"
            disabled={isSubmitting || sessions.length === 0}
            loading={isSubmitting}
            icon={parseFloat(amountInput.replace(',', '.')) < 0 ? 'ri-subtract-line' : 'ri-gas-station-line'}
          >
            {parseFloat(amountInput.replace(',', '.')) < 0 ? 'Корректировка' : 'Начислить'}
          </StatusActionButton>
        </div>
      </form>
    </BottomSheet>
  );
};

export default CompensationBottomSheet;
